import { stripHashAndSlash } from '@luna/router';
import { logger } from '@luna/logger';

import { getStoredBacklinkPathOrFromUrl } from '../backlinkPathService';

export enum SEARCH_PARAMS {
    PATH = 'path',
    CODE = 'code',
    STATE = 'state',
    REASON = 'reason',
    ID_TOKEN = 'id_token',
    OIDC = 'oidc',
}

export const getCodeFromUrlParams = (): string => {
    // fed sends query in the hash
    const hashParams = new URLSearchParams(stripHashAndSlash(window.location.hash));
    const searchparams = new URLSearchParams(window.location.search);
    return hashParams.get('code') ?? searchparams.get('code') ?? '';
};

export const getIdTokenFromUrlParams = (): string | undefined => {
    // fed sends query in the hash
    const hashParams = new URLSearchParams(stripHashAndSlash(window.location.hash));
    const searchparams = new URLSearchParams(window.location.search);
    return hashParams.get('id_token') ?? searchparams.get('id_token') ?? '';
};

export const getCsrfTokenFromUrlStateParams = (): string | undefined => {
    const hashParams = new URLSearchParams(stripHashAndSlash(window.location.hash));
    const searchparams = new URLSearchParams(window.location.search);

    try {
        return JSON.parse(searchparams.get('state') ?? hashParams.get('state') ?? '{}').csrfToken;
    } catch {
        // pass
    }

    return undefined;
};

export const getSessionStateFromUrlParams = (): string | undefined => {
    const hashParams = new URLSearchParams(stripHashAndSlash(window.location.hash));
    const searchparams = new URLSearchParams(window.location.search);
    return hashParams.get('session_state') ?? searchparams.get('session_state') ?? '';
};

export function stripOidcParamsFromUrl(): void {
    const oidcParams = [
        SEARCH_PARAMS.CODE,
        SEARCH_PARAMS.STATE,
        SEARCH_PARAMS.ID_TOKEN,
        'session_state',
    ] as const;

    const clearOidcSearchParams = (url: URL): void =>
        oidcParams.forEach((param) => url.searchParams.delete(param));

    const clearOidcHashParams = (url: URL): void => {
        if (!url.hash) return;
        const hashParams = new URLSearchParams(url.hash.substring(1));
        if (oidcParams.some((param) => hashParams.has(param))) {
            oidcParams.forEach((param) => hashParams.delete(param));
            url.hash = hashParams.toString();
        }
    };

    const url = new URL(window.location.href);
    clearOidcSearchParams(url);
    clearOidcHashParams(url);
    window.history.replaceState(null, '', url.toString());
}

export function isOIDCInUrl(): boolean {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(SEARCH_PARAMS.OIDC) === 'true';
}

export function getPathFromUrl(): string {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(SEARCH_PARAMS.PATH) || '';
}

export const getHashParamFromIframeUrl = (
    iframe: HTMLIFrameElement,
    param: string,
): string | undefined => {
    try {
        return (
            new URLSearchParams(stripHashAndSlash(iframe.contentWindow?.location.hash || '')).get(
                param,
            ) || undefined
        );
    } catch (e) {
        logger.error(e);
        return undefined;
    }
};

/**
 * We need to process current href to be viable for the redirect.
 *
 * Path from the FED OIDC can look like this for example:
 * http://localhost:3000?country=RO#code=CODE&id_token=JWT&session_state=XXX
 *
 * We need to clear the URL from un-wanted searchParams/hash (if present) and redirect the
 * path from the URL or storage.
 */
export function getLoadBusinessAppHref(): string {
    const businessUrl = new URL(window.location.href);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const [query = '', hash = hashParams.get('state') || ''] =
        getStoredBacklinkPathOrFromUrl().split('#');
    businessUrl.pathname = '/business.html';
    businessUrl.searchParams.delete(SEARCH_PARAMS.PATH);
    businessUrl.searchParams.delete(SEARCH_PARAMS.CODE);
    businessUrl.searchParams.delete(SEARCH_PARAMS.STATE);
    businessUrl.searchParams.delete(SEARCH_PARAMS.REASON);
    businessUrl.searchParams.delete(SEARCH_PARAMS.ID_TOKEN);
    new URLSearchParams(query).forEach((value, key) => {
        businessUrl.searchParams.set(key, value);
    });
    businessUrl.hash = hash;
    return businessUrl.href;
}
